import React from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';

import EditionTableToolbar from '../../common/EditionTableToolbar';
import IEventBaseDataProps from './IEventBaseDataProps';
import { IRound } from '../../../models/IEvent';
import ConfirmDialog from '../../common/ConfirmDialog';
import { format } from 'util';
import DateHelperInstance from '../../../services/DateHelper';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 280,
        },
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
    }));
interface IRoundErrors {
    name?: string;
    start?: string;
    end?: string;
}
interface IEditRoundDialogProps {
    round: IRound;
    eventDate: Date;
    // open: boolean;
    onCompleteEdit: (round: IRound) => void;
    onCancelEdit: () => void;
}
function EditRoundDialog(props: IEditRoundDialogProps) {
    const { t } = useTranslation("common");
    const [currentRound, setCurrentRound] = React.useState<IRound>(props.round);
    const [currentStart, setCurrentStart] = React.useState<string>(DateHelperInstance.dateTimePickString(props.round.start));
    const [currentEnd, setCurrentEnd] = React.useState<string>(DateHelperInstance.dateTimePickString(props.round.end));
    const [errors, setErrors] = React.useState<IRoundErrors>();
    const handleClose = () => {
        props.onCancelEdit();
    }
    const handleCompleteEdit = () => {
        const start = DateHelperInstance.parseDateTimePickString(currentStart);
        const end = DateHelperInstance.parseDateTimePickString(currentEnd);
        props.onCompleteEdit({ ...currentRound, start: start, end: end });
    }
    const checkRound = (roundToValidate: IRound, start?: string, end?: string) => {
        const error: IRoundErrors = {};
        let anyError = false;
        if (roundToValidate) {
            if (roundToValidate.name.length === 0) {
                error.name = format(t("commonErrors.requiredField"), t("rider.tag"));
                anyError = true;
            }
            if (roundToValidate.end && roundToValidate.end < roundToValidate.start) {
                error.start = t<string>("rounds.startGreaterThanEnd");
                error.end = t<string>("rounds.endLowerThanStart");
                anyError = true;
            }
            if (start && end) {
                const parsedStart = Date.parse(start);
                const parsedEnd = Date.parse(end);
                if (isNaN(parsedStart) === false && isNaN(parsedEnd) === false && parsedEnd < parsedStart) {
                    error.start = t<string>("rounds.startGreaterThanEnd");
                    error.end = t<string>("rounds.endLowerThanStart");
                    anyError = true;
                }
            }
        }
        if (anyError === true) {
            setErrors(error);
        } else {
            setErrors(undefined);
        }
    }
    return (
        <Dialog open={true} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{t("rounds.round")}</DialogTitle>
            <DialogContent>

                <TextField
                    autoFocus
                    error={Boolean(errors?.name)}
                    helperText={errors?.name}
                    margin="dense"
                    id="roundname"
                    label={t("rounds.name")}
                    type="text"
                    fullWidth
                    value={currentRound.name}
                    onChange={e => {
                        const newRound: IRound = { ...currentRound, name: e.target.value };
                        setCurrentRound(newRound);
                        checkRound(newRound);
                    }}
                />
                <TextField
                    id="roundStartTime"
                    label={t("rounds.startTime")}
                    type="datetime-local"
                    error={Boolean(errors?.start)}
                    helperText={errors?.start}
                    margin="normal"
                    fullWidth
                    value={currentStart}
                    onChange={e => {
                        setCurrentStart(e.target.value);
                        checkRound(currentRound, e.target.value, currentEnd);
                    }}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
                <TextField
                    id="roundEndTime"
                    label={t("rounds.endTime")}
                    error={Boolean(errors?.end)}
                    helperText={errors?.end}
                    fullWidth
                    type="datetime-local"
                    value={currentEnd}
                    onChange={e => {
                        setCurrentEnd(e.target.value);
                        checkRound(currentRound, currentStart, e.target.value);
                    }}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCompleteEdit} color="primary">
                    {t("commonActions.ok")}
                </Button>
                <Button onClick={handleClose} color="primary">
                    {t("commonActions.cancel")}
                </Button>
            </DialogActions>
        </Dialog>);
}

interface IEventRoundsData {
    rounds: IRound[]
}

interface IEventRoundsProps extends IEventBaseDataProps<IEventRoundsData> {
    eventDate: Date
}

function EventRounds(props: IEventRoundsProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const [selected, setSelected] = React.useState<string[]>([]);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [editingRound, setEditingRound] = React.useState<IRound | undefined>(undefined);
    const rowCount = props.data.rounds?.length ?? 0;

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleDoubleClick = (event: React.MouseEvent<unknown>, roundId: string) => {
        editRound(roundId);
    }

    const handleEdit = (event: React.MouseEvent<unknown>) => {
        if (selected.length === 1) {
            editRound(selected[0]);
        }
    }

    const editRound = (roundId: string) => {
        const round = props.data.rounds.find(g => g.id === roundId);
        if (round) {
            setEditingRound(round);
        } else {
            console.log('no round');
        }
    }

    const nextRoundId = (): number => {
        if (props.data.rounds) {
            const currentMax = Math.max(...props.data.rounds?.map(g => {
                const roundId = parseInt(g.id);
                if (isNaN(roundId)) {
                    return 0;
                } else {
                    return 1;
                }
            }));
            return currentMax + 1;
        } else {
            return 1;
        }
    }

    const handleAdd = () => {
        const roundId = nextRoundId();
        const newRound: IRound = {
            id: roundId.toString(),
            name: `${t("rounds.round")} ${roundId}`,
            start: new Date()
        };
        setEditingRound(newRound);
    }

    const handleCompleteEdit = (round: IRound) => {
        let resultingRounds: IRound[] = props.data.rounds ?? [];
        const i = resultingRounds.findIndex(r => r.id === round.id);
        if (i === -1) {
            // new round
            resultingRounds.push(round);
        } else {
            resultingRounds[i] = round;
        }
        // if there is another round with no ending date set the time just before the start of this new round
        const otherRound = resultingRounds.findIndex(r => r.id !== round.id && (r.end === undefined || r.end === null));
        if (otherRound !== -1) {
            resultingRounds[otherRound].end = new Date(round.start.getTime() - 1);
        }
        if (props.onDataChange) {
            props.onDataChange({
                rounds: resultingRounds
            });
        }
        setEditingRound(undefined);
    }


    const handleDelete = (event: React.MouseEvent<unknown>) => {
        setOpenConfirmation(true);
    }

    const deleteSelectedRounds = () => {
        setOpenConfirmation(false);
        const resultingRounds = props.data.rounds.filter(r => selected.indexOf(r.id) === -1);
        setSelected([]);
        if (props.onDataChange) {
            props.onDataChange({
                rounds: resultingRounds
            });
        }
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = props.data.rounds.map((n) => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };
    const isSelected = (name: string) => selected.indexOf(name) !== -1;
    const handleCloseConfirmation = () => {
        setOpenConfirmation(false);
    };

    return (
        <React.Fragment>
            <ConfirmDialog
                open={openConfirmation}
                onAccept={deleteSelectedRounds}
                onCancel={handleCloseConfirmation}
                title={t("rounds.deleteRounds")}
                message={format(t("rounds.confirmDelete"), selected.length.toString())} />

            {editingRound === undefined ? (<></>) : (<EditRoundDialog
                round={editingRound}
                eventDate={props.eventDate}
                onCompleteEdit={handleCompleteEdit}
                onCancelEdit={() => setEditingRound(undefined)} />)}
            <Paper className={classes.paper}>

                <EditionTableToolbar
                    numSelected={selected.length}
                    headerLabel={t("eventsetup.rounds")}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAdd={handleAdd} />
                <TableContainer component={Paper}>
                    <Table className={classes.table} aria-label="rounds">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < rowCount}
                                        checked={rowCount > 0 && selected.length === rowCount}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                                <TableCell align="left" >{t("rounds.name")}</TableCell>
                                <TableCell align="right">{t("rounds.startTime")}</TableCell>
                                <TableCell align="right">{t("rounds.endTime")}</TableCell>

                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.data.rounds?.map((round, index) => {
                                const isItemSelected = isSelected(round.id);
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return (
                                    <TableRow key={round.id}
                                        hover
                                        onClick={(event) => handleClick(event, round.id)}
                                        onDoubleClick={(event) => handleDoubleClick(event, round.id)}
                                        role="checkbox"
                                        tabIndex={-1}
                                        selected={isItemSelected}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isItemSelected}
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </TableCell>
                                        <TableCell component="th" scope="row" id={labelId}>
                                            {round.name}
                                        </TableCell>
                                        <TableCell align="right">{DateHelperInstance.formatDateTime(round.start)}</TableCell>
                                        <TableCell align="right">{DateHelperInstance.formatDateTime(round.end)}</TableCell>

                                    </TableRow>)
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </React.Fragment>);
}

export default EventRounds;